import React, { Component } from 'react';
import { Text } from '@components-library/Typography';
import { FormValuesType, IFormField } from '@components-shared/forms/formField.interface';
import { ITableFilter, ITableFilterFieldType } from '@components-shared/table/TableFilter';
import TableFilter from '@components-shared/table/TableFilter/TableFilter';
import { TableFootnote } from '@components-shared/table/TableFootnote';
import { TablePagination } from '@components-shared/table/TablePagination/TablePagination';
import { TableRowSelectionButton } from '@components-shared/table/TableRowSelectionButton/TableRowSelectionButton';
import { TableWrapper } from '@components-shared/table/TableWrapper';
import { DEFAULT_HEADER_SORT_VALUES, NUMBER_OF_ROWS, TABLE_CACHE_PREFIX } from '@components-shared/table/constants';
import { updateTablePageQueryParams } from '@components-shared/table/utils/updateTablePageQueryParams';
import { withRouterInnerRef } from '@components-shared/withRouterInnerRef';
import { keyMap } from '@locales/lokalise/keyMap';
import { cacheService } from '@utils/library';
import { CancelToken, CancelTokenSource, fetchApi, isFetchApiCancel } from 'api/fetchApi';
import cn from 'classnames';
// @ts-ignore: remove after translating into typescript
import { getInitialState } from 'components/shared/forms/formGenerator';
import Preloader from 'components/shared/preloader';
import { getUpdatedSelectedRows } from 'components/shared/table/rowSelection';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { ITableRecordData } from 'global.interfaces';
import { ErrorType, handleError } from 'utils/errors';
import { getURLParams } from 'utils/helper';
import ActionModal from './ActionModal';
import { TableBody } from './TableBody';
import TableExport from './TableExport';
import TableHeader from './TableHeader';
import {
  RowSelectionAction,
  TableColumn,
  TableCurrentRow,
  TableGetDataResponse,
  TableProps,
  TableState,
} from './types';

export type TableRefObject = InstanceType<typeof Table>;

/**
 * @deprecated Please use the new grid for new components.
 */
class Table<ExtraTableProps = Record<string, unknown>> extends Component<TableProps & ExtraTableProps, TableState> {
  tableId: string;

  constructor(props: TableProps & ExtraTableProps) {
    super(props);

    const { columns, tableId } = this.props;
    const headerFilters = columns
      .filter(({ filterType }) => filterType)
      .map(({ key, filterType }) => ({ name: key, type: filterType }));
    const headerFiltersStateInitialState = { ...getInitialState(headerFilters) };

    this.tableId = `${tableId}-page`;

    const numberOfRows = this.setNumberOfRows();

    this.state = {
      headerFilterValues: headerFiltersStateInitialState,
      headerSortValues: DEFAULT_HEADER_SORT_VALUES,
      headerFilterDefaultValues: headerFiltersStateInitialState,
      numberOfRows,
      processing: true,
      startIndex: 0,
      count: 0,
      preciseCount: null,
      totalsData: null,
      data: [],
      initialTableData: [],
      showSettings: true,
      updating: false,
      currentRow: null,
      noData: undefined,
      selectedRowsIds: [],
      hideActionShadow: true,
      hasTableScroll: true,
    };
  }

  filter = React.createRef<ITableFilter>();

  tableRef = React.createRef<HTMLTableElement>();

  source?: CancelTokenSource;

  async componentDidMount() {
    const { apiExplicitRequest, location = {} } = this.props;
    const { search = null } = location;

    if (!apiExplicitRequest) {
      const urlParams = getURLParams(search);
      const page = urlParams[this.tableId];
      const action = page ? () => this.changePage(+page - 1) : this.getData;

      const initialTableData = await action();

      this.setState({ initialTableData });
    } else {
      this.setState({
        processing: false,
        showSettings: false,
        noData: true,
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<TableProps & ExtraTableProps>) {
    if (prevProps.api !== this.props.api) {
      this.setState({ numberOfRows: this.setNumberOfRows() });
    }
  }

  componentWillUnmount(): void {
    this.cancelPreviousRequest();
  }

  setNumberOfRows = () => {
    const { tableId, api } = this.props;

    return cacheService.getCachedValue<number>(tableId, TABLE_CACHE_PREFIX, api) || NUMBER_OF_ROWS[0];
  };

  getCurrentCount = () => {
    const { count, preciseCount } = this.state;

    return preciseCount ?? count;
  };

  getFetchBody = () => {
    const { initialSortOrder } = this.props;
    const { headerFilterValues, headerSortValues } = this.state;
    const filter = this.filter?.current?.getFilter?.() ?? {};

    return {
      ...filter,
      ...headerFilterValues,
      headerSortValues,
      ...(initialSortOrder ? { orderBy: [initialSortOrder] } : {}),
    };
  };

  getData = async (skip = 0, clearData = true): Promise<ITableRecordData[]> => {
    const { numberOfRows, headerFilterValues, headerSortValues } = this.state;
    const { api, onGetDataTrigger, onGetDataHandler } = this.props;

    if (skip < 0) {
      skip = 0;
    }

    this.setState({
      processing: clearData,
      updating: !clearData,
    });

    const filter = this.filter?.current?.getFilter?.() ?? {};
    const perPage = numberOfRows;

    const startSymbol = api && api.toString().indexOf('?') === -1 ? '?' : '&';
    const apiString = `${api}${startSymbol}skip=${skip}&take=${perPage}`;

    if (onGetDataHandler) {
      onGetDataHandler({
        ...(filter as never as { [name: string]: FormValuesType }),
        ...headerFilterValues,
        ...headerSortValues,
      });
    }

    this.cancelPreviousRequest();

    this.source = CancelToken.source();
    const fetchBody = this.getFetchBody();

    try {
      const fetchFn = fetchApi.post<TableGetDataResponse>(apiString, fetchBody, { cancelToken: this.source.token });

      const { data: responseData } = await fetchFn;

      const data: TableGetDataResponse = responseData;
      const isPagination = !!data?.records || !!(data as any)?.data?.records;
      const count = data?.count || (data as any).data?.recordsCount || (data as any).length || 0;
      const tableData = data.records || ((data as any)?.data && (data as any)?.data.records) || data;

      this.props.gridApiCallback?.(responseData);
      this.setState({
        data: tableData,
        count,
        processing: false,
        updating: false,
        noData: false,
        numberOfRows: isPagination ? perPage : this.getCurrentCount(),
        showSettings: isPagination,
      });

      if (onGetDataTrigger) {
        onGetDataTrigger(data.records || data);
      }

      return tableData;
    } catch (error) {
      if (!isFetchApiCancel(error as ErrorType)) {
        handleError(keyMap.sharedErrorsFailedToGetData, error as ErrorType);
        this.setState({ processing: false, updating: false, noData: false });
      }
      return [];
    }
  };

  cancelPreviousRequest(): void {
    if (this.source) {
      this.source.cancel('Operation canceled due to new request.');
    }
  }

  updateTablePageQueryParamsDependOnApiExplicitRequest = (pageNumber: number): void => {
    const { apiExplicitRequest, history, location = {} } = this.props;

    if (apiExplicitRequest) {
      return;
    }
    updateTablePageQueryParams(pageNumber, this.tableId, location, history);
  };

  changePage = (page: number, currentNumberOfRow?: number): ITableRecordData[] => {
    const numberOfRows = currentNumberOfRow ?? this.state.numberOfRows;
    const pageNumber = page < 0 ? 0 : page;
    const startIndex = pageNumber * numberOfRows;

    this.setState({ startIndex }, async () => {
      this.updateTablePageQueryParamsDependOnApiExplicitRequest(pageNumber);
      await this.getData(startIndex);
    });

    return [];
  };

  updateData = async (clearData = true, deletedCount = 0): Promise<void> => {
    const { startIndex, numberOfRows } = this.state;

    const currentCount = this.getCurrentCount();

    if (currentCount - deletedCount < startIndex) {
      const lastPage = currentCount ? Math.ceil((currentCount - deletedCount) / numberOfRows) - 1 : 0;

      await this.changePage(lastPage);
      await this.getData(startIndex - numberOfRows, clearData);
    } else {
      await this.getData(startIndex, clearData);
    }
  };

  setUpdatingState = (state = true): void => this.setState({ updating: state });

  setCurrentRow = (currentRow: TableCurrentRow | null = null): void => this.setState({ currentRow });

  getColumns = (): TableColumn[] => {
    const { parentContext, columns } = this.props;

    return columns.filter(
      (col) => !col.isHidden || (typeof col.isHidden === 'function' && !col.isHidden(parentContext)),
    );
  };

  renderTableTitle = (): JSX.Element | null => {
    const { title } = this.props;

    return !title ? null : <h3 className="table__title">{title}</h3>;
  };

  toggleRowsSelection = (selectedRowIds: number[]): void => {
    const { selectedRowsIds } = this.state;
    const newSelectedRowsIds = getUpdatedSelectedRows(selectedRowsIds, selectedRowIds);

    this.setState({ selectedRowsIds: newSelectedRowsIds });
  };

  updateTableScroll = (hasTableScroll: boolean) => {
    this.setState({ hasTableScroll });
  };

  updateActionShadow = (hideActionShadow: boolean) => {
    this.setState({ hideActionShadow });
  };

  renderTable(): JSX.Element {
    const idColumn = 'id';
    const {
      className,
      actions,
      parentContext,
      pagination = true,
      actionKey,
      showPreloader,
      additionalActionsClass = '',
      tableHasExtensions = false,
      toggleOpened,
      tableRowDetails = () => null,
      openedRowDetailsId,
      selectByColumnName = idColumn,
      apiGetTotals,
      useStickyActions,
      api,
      truncatePagination,
      tableId,
    } = this.props;

    const {
      processing,
      noData,
      data,
      initialTableData,
      startIndex,
      showSettings,
      updating,
      headerFilterValues,
      headerFilterDefaultValues,
      headerSortValues,
      selectedRowsIds,
      totalsData,
      hideActionShadow,
      hasTableScroll,
      numberOfRows,
    } = this.state;

    if (processing || showPreloader) {
      return <Preloader />;
    }

    if (noData) {
      return (
        <div className="table__container">
          <Text>{keyMap.tableClickFilterButton}</Text>
        </div>
      );
    }

    const additionalClass = className || '';
    const columns = this.getColumns();

    const withStickyAction = Boolean(useStickyActions && !hideActionShadow && this.state.hasTableScroll);

    return (
      <div className="table__container">
        {this.renderTableTitle()}
        <TableWrapper
          updateActionShadow={this.updateActionShadow}
          hideActionShadow={hideActionShadow}
          hasTableScroll={hasTableScroll}
          useStickyActions={useStickyActions}
          tableRef={this.tableRef}
          updateTableScroll={this.updateTableScroll}>
          <table
            ref={this.tableRef}
            className={cn([`table ${additionalClass}`], {
              'table--disabled': updating,
              'extended': tableHasExtensions,
            })}>
            <TableHeader
              columns={columns}
              actions={actions}
              toggleRowsSelection={this.toggleRowsSelection}
              data={data}
              initialTableData={initialTableData}
              selectedRowsIds={selectedRowsIds}
              headerFilterValues={headerFilterValues}
              headerFilterDefaultValues={headerFilterDefaultValues}
              tableHasExtensions={tableHasExtensions}
              onFilter={this.setHeaderFilterValuesState}
              getSortedData={this.getSortedData}
              headerSortValues={headerSortValues}
              selectByColumnName={selectByColumnName}
              changePage={this.changePage}
              withStickyAction={withStickyAction}
            />

            <TableBody
              actionKey={actionKey}
              data={data}
              columns={columns}
              actions={actions}
              startIndex={startIndex}
              parentContext={parentContext}
              updateData={this.updateData}
              setUpdatingState={this.setUpdatingState}
              setCurrentRow={this.setCurrentRow}
              additionalActionsClass={additionalActionsClass}
              selectedRowsIds={selectedRowsIds}
              toggleRowsSelection={this.toggleRowsSelection}
              openedRowDetailsId={openedRowDetailsId}
              toggleOpened={toggleOpened}
              tableHasExtensions={tableHasExtensions}
              tableRowDetails={tableRowDetails}
              selectedColumn={headerSortValues.selectedColumn}
              sortingType={headerSortValues.sortingType}
              selectByColumnName={selectByColumnName}
              totals={totalsData ? apiGetTotals?.totalRowAmountsExtractor?.(totalsData) : undefined}
              withStickyAction={withStickyAction}
            />
          </table>
        </TableWrapper>

        <div className="table__footer-container">
          {pagination && (
            <TablePagination
              apiGetTotals={apiGetTotals}
              truncatePagination={truncatePagination}
              startIndex={startIndex}
              count={this.getCurrentCount()}
              changePage={this.changePage}
              numberOfRows={numberOfRows}
              getFetchBody={this.getFetchBody}
              updateTableState={(state) => this.setState((prev) => ({ ...prev, ...state }))}
              api={api}
              showSettings={showSettings}
              totalsData={totalsData}
              tableId={tableId}
            />
          )}
        </div>
        {this.props.footnote && <TableFootnote icon={this.props.footnote.icon} hint={this.props.footnote.hint} />}
        {Boolean(totalsData) && this.props.apiGetTotals?.totalComponent?.(totalsData)}
      </div>
    );
  }

  getSortedData = (selectedColumn: string, sortingType: boolean): void => {
    const { headerSortValues } = this.state;

    if (headerSortValues.selectedColumn === selectedColumn && headerSortValues.sortingType === sortingType) {
      this.setHeaderSortValuesState(DEFAULT_HEADER_SORT_VALUES.selectedColumn, DEFAULT_HEADER_SORT_VALUES.sortingType);
    } else {
      this.setHeaderSortValuesState(selectedColumn, sortingType);
    }
  };

  resetTotalsData = () => {
    this.setState({ totalsData: null, preciseCount: null });
  };

  setHeaderSortValuesState = (selectedColumn: string, sortingType: boolean): void => {
    this.resetTotalsData();
    this.setState({ headerSortValues: { selectedColumn, sortingType } }, () => this.changePage(0));
  };

  setHeaderFilterValuesState = (headerFilterValues: TableState['headerFilterValues']): void => {
    this.resetTotalsData();
    this.setState({ headerFilterValues }, () => this.changePage(0));
  };

  handlePressFilter = () => {
    this.resetTotalsData();
    this.setState({ totalsData: null, preciseCount: null }, () => this.changePage(0));
  };

  getFilterFields = (
    parentContext: unknown,
    filterFields?: Array<ITableFilterFieldType>,
  ): ITableFilterFieldType[] | void => {
    return filterFields?.filter((f) => !f.isHidden || (typeof f.isHidden === 'function' && !f.isHidden(parentContext)));
  };

  onRowSelectionButtonClick = (action: RowSelectionAction): void => {
    const { selectedRowsIds } = this.state;

    action.onClick(selectedRowsIds);
    this.setState({ selectedRowsIds: [] });
  };

  render(): JSX.Element {
    const { rowSelectionActions, filterFields, exportAPI, filterClassName, parentContext, beforeBtn, exportEvent } =
      this.props;
    const { currentRow, processing, selectedRowsIds } = this.state;

    const fields = this.getFilterFields(parentContext, filterFields);

    return (
      <div className="table-component">
        {!fields && exportAPI && <TableExport exportAPI={exportAPI} />}

        {fields ? (
          <TableFilter
            fields={fields}
            onFilter={this.handlePressFilter}
            ref={this.filter}
            className={filterClassName}
            exportAPI={exportAPI}
            parentContext={parentContext}
            exportEvent={exportEvent}
            processing={processing}
          />
        ) : (
          <></>
        )}

        <TableRowSelectionButton
          rowSelectionActions={rowSelectionActions}
          onRowSelectionButtonClick={this.onRowSelectionButtonClick}
          selectedRowsIds={selectedRowsIds}
        />

        {beforeBtn && (
          <button
            type="button"
            className={`table__before-btn btn ${beforeBtn.className || ''}`}
            onClick={beforeBtn.onClick}>
            {beforeBtn.title}
          </button>
        )}

        {this.renderTable()}

        {!processing && currentRow && (
          <ActionModal
            closeModal={() => this.setCurrentRow(null)}
            {...currentRow}
            fields={currentRow.fields as IFormField[]}
          />
        )}
      </div>
    );
  }
}

export { Table };

export default withRouterInnerRef(withErrorBoundary(Table));
